<script lang="ts" setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import type { SearchResultItem } from '@/models';
import { globalSearch } from '@/services';

const route = useRoute();
const router = useRouter();

const searchInputRef = ref<HTMLInputElement | null>(null);

const searchQuery = ref('');
const showResults = ref(false);
const filteredResults = ref<SearchResultItem[]>([]);
const selectedIndex = ref(-1);

function getItemLink(item: SearchResultItem) {
  if (item.type === 'Locomotive') {
    return { name: 'locomotives.details', params: { id: item.itemId } };
  } else if (item.type === 'Car') {
    return { name: 'cars.details', params: { id: item.itemId } };
  }
  return '/';
}

function clearSearch() {
  searchQuery.value = '';
  showResults.value = false;
  filteredResults.value = [];
}

const debouncedSearch = useDebounceFn(async () => {
  const q = searchQuery.value.trim();
  if (!q) {
    filteredResults.value = [];
    showResults.value = false;
    return;
  }


  const results = await globalSearch(q);
  filteredResults.value = results;
  showResults.value = true;
}, 500);


function onSearchInput() {
  debouncedSearch();
}

function handleKeyDown(evt: KeyboardEvent) {
  if (!showResults.value || filteredResults.value.length === 0) {
    return;
  }

  if (evt.key === 'ArrowDown') {
    evt.preventDefault();
    selectedIndex.value = (selectedIndex.value + 1) % filteredResults.value.length;
  } else if (evt.key === 'ArrowUp') {
    evt.preventDefault();
    selectedIndex.value = (selectedIndex.value - 1 + filteredResults.value.length) % filteredResults.value.length;
  } else if (evt.key === 'Enter') {
    evt.preventDefault();
    if (selectedIndex.value >= 0 && selectedIndex.value < filteredResults.value.length) {
      const selectedItem = filteredResults.value[selectedIndex.value];
      router.push(getItemLink(selectedItem));
      clearSearch();
    }
  }
}

function handleGlobalKeyDown(evt: KeyboardEvent) {
  // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
  // if ((evt.ctrlKey || evt.metaKey) && evt.key === 'k') {

  if (evt.key === '/') {
    // Don't trigger if user is typing in an input, textarea or other editable element
    const target = evt.target as HTMLElement;
    if (target.tagName == 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    evt.preventDefault();
    searchInputRef.value?.focus();
    searchInputRef.value?.select();
  }
}

// Hide results on route change
watch(() => route.fullPath, () => {
  clearSearch();
});

// Reset selected index when search results change
watch(filteredResults, () => {
  selectedIndex.value = -1;
});

onMounted(() => {
  document.addEventListener('keydown', handleGlobalKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeyDown);
});
</script>

<template>
  <div class="relative flex flex-1 justify-center px-2 lg:ml-6 lg:justify-end">
    <div class="w-3/4 lg:w-full max-w-lg lg:max-w-xs">
      <div class="relative">
        <input
          v-model="searchQuery"
          ref="searchInputRef"
          @keyup.escape="clearSearch"
          @keydown="handleKeyDown"
          @input="onSearchInput"
          name="search"
          type="text"
          placeholder="Quick Search"
          aria-label="Quick Search"
          class="block w-full rounded-md bg-white/5 py-1.5 pr-3 pl-10 text-base text-white outline -outline-offset-1 outline-white/10 placeholder:text-gray-300 focus:text-white focus:outline-2"
          :class="{'rounded-b-none': showResults }"
        />
        <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-1/2 transform -translate-y-1/2 size-5 text-gray-300" aria-hidden="true" />

        <kbd v-if="!searchQuery" class="hidden lg:inline-flex pointer-events-none absolute right-3 top-1/2 transform -translate-y-1/2 items-center rounded border border-gray-400/30 px-1.5 py-0.5 font-sans text-xs text-gray-400">/</kbd>

        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors"
          aria-label="Clear search"
        >
          <XMarkIcon class="size-5" />
        </button>


        <Transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 scale-95 transform -translate-y-1"
          enter-to-class="opacity-100 scale-100 transform translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 scale-100 transform translate-y-0"
          leave-to-class="opacity-0 scale-95 transform -translate-y-1"
        >
          <div v-if="showResults && filteredResults.length" class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-md shadow-lg z-50 max-h-80 overflow-auto">
            <ul>
              <li v-for="(item, index) in filteredResults" :key="item.type + '-' + item.itemId">
                <RouterLink
                  :to="getItemLink(item)"
                  class="block px-4 py-2 hover:bg-sky-100 text-gray-900 border-b last:border-b-0 border-gray-100"
                  :class="{ 'bg-sky-100': selectedIndex === index }"
                  @click="clearSearch"
                >
                  <div class="font-semibold">{{ item.title }}</div>
                  <div class="text-xs text-gray-500">{{ item.description }} ({{ item.type }})</div>
                </RouterLink>
              </li>
            </ul>
          </div>
          <div v-else-if="showResults && filteredResults.length === 0" class="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-b-md shadow-lg z-50 max-h-80">
            <div class="px-4 py-2 text-gray-500">
              No results found.
            </div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>
